import { __ } from '@/hub-shared/utils/i18n';

const MISSION_CONTEXT_SEPARATOR = ' · ';

export const extractCompanyNameFromMissionContext = (
  missionContextLabel: string | null | undefined,
): string => {
  const context = missionContextLabel?.trim() ?? '';
  if (context === '') {
    return '';
  }

  const separatorIndex = context.indexOf(MISSION_CONTEXT_SEPARATOR);
  if (separatorIndex === -1) {
    return context;
  }

  return context.slice(0, separatorIndex).trim();
};

/**
 * Company-scoped events (e.g. `profile.public_source.import_failed`) carry no mission context,
 * so there is no `%s` company name to interpolate into a template — they deliberately fall
 * through to `default: null` below and render through {@link formatNotificationInboxTitle}'s
 * fallback (the event's static label from `NOTIFICATION_EVENT_LABELS`). Do not add a template
 * case for them; add the label instead.
 */
const inboxTitleTemplateForEvent = (eventCode: string): string | null => {
  switch (eventCode) {
    case 'mission.ready_to_generate':
      return __('Vous pouvez générer le rapport VS de %s');
    case 'mission.ready_to_revise':
      return __('Vous pouvez réviser le rapport VS de %s');
    case 'mission.renewable':
      return __('Vous pouvez renouveler le rapport VS de %s');
    case 'mission.ready_for_review':
      return __('Vous pouvez superviser le rapport VS de %s');
    case 'mission.ready_to_close':
      return __('Vous pouvez clôturer le rapport VS de %s');
    case 'mission.ready_to_sign':
      return __('Vous pouvez signer le rapport VS de %s');
    case 'mission.ready_to_archive':
      return __('Vous pouvez archiver le rapport VS de %s');
    case 'mission.assigned':
      return __('Vous avez été assigné à la mission VS de %s');
    case 'diligence.note.published':
      return __('Un commentaire a été publié sur le rapport VS de %s');
    case 'diligence.anomaly_created':
      return __('Une anomalie a été signalée sur le rapport VS de %s');
    default:
      return null;
  }
};

export const formatNotificationInboxTitle = (
  eventCode: string,
  missionContextLabel: string | null | undefined,
  fallbackLabel: string,
): string => {
  const companyName = extractCompanyNameFromMissionContext(missionContextLabel);
  const template = inboxTitleTemplateForEvent(eventCode);

  if (template !== null && companyName !== '') {
    return template.replace('%s', companyName);
  }

  return formatNotificationRowTitle(fallbackLabel, missionContextLabel);
};

/** @deprecated Use {@link formatNotificationInboxTitle} for inbox rows. */
export const formatNotificationRowTitle = (
  eventLabel: string,
  missionContextLabel: string | null | undefined,
): string => {
  const label = eventLabel.trim();
  const context = missionContextLabel?.trim() ?? '';

  if (label === '') {
    return context;
  }

  if (context === '') {
    return label;
  }

  return `${label} · ${context}`;
};
